import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { TRPCClientError } from "@trpc/client";
import { trpc } from "@utils/trpc";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";
import { useData } from "../../runtime/provider";

export const merchOrderQueryKey = (accessId: string) =>
  getQueryKey(trpc.user.accesses.viewMerchOrder, { accessId }, "query");

/**
 * The signed-in holder's orders of one merch product, with the address each
 * is posted to. Kept in memory only, not in the durable cache: the address
 * is personal, and the wallet's own read (`useAccessTickets`, durable)
 * already holds everything else the order screen shows offline.
 */
export const useMerchOrder = (accessId: string | undefined) => {
  const data = useData();
  return useQuery({
    queryKey: merchOrderQueryKey(accessId ?? ""),
    queryFn: async () => {
      await waitForAuthReady(data.auth);
      return data.api.user.accesses.viewMerchOrder.query({
        accessId: accessId!,
      });
    },
    enabled: !!accessId,
    refetchOnMount: "always",
    retry: (failures, error) =>
      // Not found is an answer: this account holds no order of it.
      !(error instanceof TRPCClientError && error.data?.code === "NOT_FOUND") &&
      failures < 2,
  });
};
